import { useRef } from "react";
import type { PointerEvent } from "react";

interface PointerDragOptions<Start> {
  /** При натискане: мерене на началото (ширини, височини); null — без влачене. */
  onStart: (event: PointerEvent<HTMLElement>) => Start | null;
  /** При всяко мръдване — изместването от натискането. */
  onMove: (start: Start, deltaX: number, deltaY: number) => void;
  /** При пускане (и при прекъснато влачене). */
  onEnd: () => void;
}

/**
 * Влачене на дръжка с мишка или пръст (Pointer Events): хващането е върху
 * самата дръжка (`setPointerCapture`), затова движението се следи и когато
 * показалецът излезе от нея, а курсорът остава този на дръжката. Връща
 * props-ите за дръжката (дръжките са невидими — няма какво да се оцветява).
 */
export const usePointerDrag = <Start>({ onStart, onMove, onEnd }: PointerDragOptions<Start>) => {
  const dragRef = useRef<{ pointerId: number; x: number; y: number; start: Start } | null>(null);

  const finish = () => {
    if (!dragRef.current) return;
    dragRef.current = null;
    onEnd();
  };

  const handleProps = {
    onPointerDown: (event: PointerEvent<HTMLElement>) => {
      if (event.button !== 0) return;
      const start = onStart(event);
      if (start === null) return;
      // Без маркиране на текст и без фокус/клик под дръжката.
      event.preventDefault();
      event.stopPropagation();
      event.currentTarget.setPointerCapture(event.pointerId);
      dragRef.current = { pointerId: event.pointerId, x: event.clientX, y: event.clientY, start };
    },
    onPointerMove: (event: PointerEvent<HTMLElement>) => {
      const drag = dragRef.current;
      if (!drag || drag.pointerId !== event.pointerId) return;
      onMove(drag.start, event.clientX - drag.x, event.clientY - drag.y);
    },
    onPointerUp: finish,
    onPointerCancel: finish,
    onLostPointerCapture: finish,
  };

  return { handleProps };
};
